import { store } from '@/app/store';
import { useGetMarketChartQuery } from '@/features/marketChart/marketChartApi';
import { selectAllMarketInfo, selectMarketInfoById, useGetMarketInfoQuery } from '@/features/marketInfo/marketInfoApi';
import { selectAllMarkets, selectMarketById } from '@/features/markets/marketsApi';
import { Market } from '@/types/markets';
import { normalizedBig } from '@cex/shared-modules';
import Big from 'big.js';
import { first, last } from 'lodash';
import { useTrades } from './trades';
import { getPercentFormat } from '@/shared/helpers/formatters';

export const useMarket = (marketId?: string) => {
  return {
    market: marketId ? selectMarketById(store.getState(), marketId) : null,
    markets: selectAllMarkets(store.getState()),
    query: useGetMarketInfoQuery(undefined, {
      refetchOnMountOrArgChange: true
    })
  };
};

export const useMarketInfo = (marketId?: string) => {
  return {
    marketInfo: marketId ? selectMarketInfoById(store.getState(), marketId) : null,
    marketsInfo: selectAllMarketInfo(store.getState()),
    query: useGetMarketInfoQuery(undefined, {
      refetchOnMountOrArgChange: true
    })
  };
};

export const useMarketChartData = (market: string): number[] => {
  const { data: chartData } = useGetMarketChartQuery(market);

  const marketChartData = chartData?.entities[market];

  if (!marketChartData || !marketChartData.data) {
    return [];
  }

  return marketChartData.data;
};

export const useLastPrice = (market: string) => {
  const trades = useTrades(market);

  if (!trades) {
    return Big(0);
  }

  return Big(trades[0]?.price || 0);
};

export const useTrend = (marketChartsData?: number[], market?: Market) => {
  if (!market || !marketChartsData) {
    return Big(0);
  }

  const firstPrice = normalizedBig(first(marketChartsData));
  const lastPrice = normalizedBig(last(marketChartsData));

  if (!firstPrice.gt(0)) {
    return Big(0);
  }
  return lastPrice.minus(firstPrice).div(firstPrice).times(100);
};

export const formatValue = (amount: Big, fractionDigits) => {
  return amount.toFixed(fractionDigits);
};

export const useMarketPriceDiffFormatted = (trend: Big): string => getPercentFormat(trend.div(100));
